import { useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { ConfigProvider } from "antd5";
import { Text } from "styles/utility-components";

import { ExportNoticesButton } from "components/notices/ExportNoticesButton";
import { NoticeTable } from "components/notices/NoticeTable";
import { EMPTY_FILTERS, NoticeColumns, NoticeFilters } from "components/notices/utils";
import EmptyState from "components/ui/empty_state/EmptyState";
import Modal from "components/ui/modal/Modal";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import Tabs, { TabItem } from "components/ui/tabs/Tabs";
import { useBuyerSupplierAwardPeriodNotices } from "lib/hooks/api/buyer/useBuyerSupplierAwardPeriodNotices";
import { SpendFilters, supplierSpendFilter } from "lib/hooks/api/spend/spendFilters";
import { SortState } from "lib/search/types";
import { SupplierMentionType } from "lib/types/graphQLEnums";
import { BuyerSupplierTransactions } from "./BuyerSupplierTransactions";
import { formatSpendValue } from "./spendChartRows";

export type PeriodTab = "awards" | "transactions";

/** One clicked chart period, with the totals its bar and line showed. */
export type ChartPeriod = {
  /** The chart's label for it, e.g. "Mar 2025". */
  label: string;
  /** The chart's interval, which with `start` names the bar the awards came from. */
  interval: "month" | "quarter" | "year";
  /** The period's own first day, before clamping, as YYYY-MM-DD. */
  start: string;
  /** First day it covers, clamped to the page's range, as YYYY-MM-DD. */
  from: string;
  /** Last day it covers, as YYYY-MM-DD. */
  to: string;
  awardCount: number;
  awardValue: number;
  transactionCount: number;
};

type Props = {
  buyerGuid: string;
  supplierGuid: string;
  buyerName?: string;
  supplierName?: string;
  /** Which awards count, so the list matches the bar that was clicked. */
  supplierMentionType: SupplierMentionType;
  /** The page's start date, which the chart's first bar was cut at. */
  publishedFrom?: string;
  /** Without spend access there is no transactions tab. */
  hasSpendAccess: boolean;
  period: ChartPeriod;
  /** The tab for the series that was clicked. */
  initialTab: PeriodTab;
  onClose: () => void;
};

const AWARD_COLUMNS: NoticeColumns[] = ["title", "stage", "value", "published_date", "award_date"];

/** Clear of the viewport on every side, so the page behind still frames the modal. */
const VIEWPORT_GAP = 80;
const MAX_WIDTH = 1280;
/** Tall enough that a period with a row or two doesn't collapse to a strip. */
const MIN_HEIGHT = 560;
const MAX_HEIGHT = `calc(100vh - ${VIEWPORT_GAP * 2}px)`;

/**
 * What made up one period of the buyer-supplier chart: the awards its bar counted and the
 * transactions its line summed. Clicking an award opens its preview on top, and closing that
 * returns here.
 */
export function BuyerSupplierPeriodModal({
  buyerGuid,
  supplierGuid,
  buyerName,
  supplierName,
  supplierMentionType,
  publishedFrom,
  hasSpendAccess,
  period,
  initialTab,
  onClose,
}: Props) {
  const tabs: TabItem[] = [
    {
      key: "awards",
      label: "Awards",
      badge: String(period.awardCount),
      children: (
        <AwardsTab
          buyerGuid={buyerGuid}
          supplierGuid={supplierGuid}
          supplierMentionType={supplierMentionType}
          publishedFrom={publishedFrom}
          period={period}
        />
      ),
    },
  ];
  if (hasSpendAccess) {
    const filters: SpendFilters = {
      ...supplierSpendFilter({ buyerGuid, supplierGuid }),
      dateFrom: period.from,
      dateTo: period.to,
    };
    tabs.push({
      key: "transactions",
      label: "Transactions",
      badge: String(period.transactionCount),
      children: (
        <TabBody>
          <BuyerSupplierTransactions filters={filters} periodLabel={period.label} />
        </TabBody>
      ),
    });
  }

  return (
    <PeriodModalFrame
      title={period.label}
      subtitle={supplierName && buyerName ? `${buyerName} and ${supplierName}` : buyerName}
      tabs={tabs}
      defaultTab={hasSpendAccess ? initialTab : "awards"}
      onClose={onClose}
    />
  );
}

type FrameProps = {
  title: string;
  subtitle?: string;
  tabs: TabItem[];
  defaultTab: string;
  onClose: () => void;
};

/**
 * A wide modal pinned near the top of the viewport, with a title and tabs above a full-width
 * border and only the tab content scrolling below it.
 */
export function PeriodModalFrame({ title, subtitle, tabs, defaultTab, onClose }: FrameProps) {
  const { layoutRef, minHeight } = useNeverShrinkingHeight();

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width={`calc(100vw - ${VIEWPORT_GAP * 2}px)`}
      maxWidth={`${MAX_WIDTH}px`}
      offsetTop={`${VIEWPORT_GAP}px`}
      padding={0}
    >
      {/* Ant popups default to document.body, which a native modal dialog leaves inert, so keep
          them inside the dialog where they can be clicked. */}
      <ConfigProvider getPopupContainer={popupContainer}>
        <Layout ref={layoutRef} style={{ minHeight }}>
          <Header>
            <Text h1>{title}</Text>
            {subtitle && <Text color="sysTextSecondary">{subtitle}</Text>}
          </Header>
          {/* The tab bar's own full-width border divides the header from the content, which is the only part that scrolls. */}
          <TabsArea>
            <Tabs items={tabs} defaultValue={defaultTab} />
          </TabsArea>
        </Layout>
      </ConfigProvider>
    </Modal>
  );
}

function popupContainer(trigger?: HTMLElement): HTMLElement {
  return trigger?.closest("dialog") ?? document.body;
}

/**
 * Keeps the modal at the tallest height it has reached, so switching to a shorter tab or page, or
 * a tab that's still loading, doesn't make it jump. It starts at MIN_HEIGHT and never exceeds the
 * viewport, which scrolls the content instead.
 */
function useNeverShrinkingHeight() {
  const layoutRef = useRef<HTMLDivElement>(null);
  const [tallest, setTallest] = useState(0);

  useLayoutEffect(() => {
    const node = layoutRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      const height = node.getBoundingClientRect().height;
      setTallest((current) => (height > current ? height : current));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { layoutRef, minHeight: `min(${Math.max(MIN_HEIGHT, tallest)}px, ${MAX_HEIGHT})` };
}

type AwardsTabProps = {
  buyerGuid: string;
  supplierGuid: string;
  supplierMentionType: SupplierMentionType;
  publishedFrom?: string;
  period: ChartPeriod;
};

function AwardsTab(props: AwardsTabProps) {
  if (props.period.awardCount === 0) {
    return (
      <TabBody>
        <EmptyState
          type="noResults"
          heading={`No awards in ${props.period.label}`}
          description={`Nothing was awarded between this buyer and supplier in ${props.period.label}.`}
          width="100%"
          yAxisMargin="64px"
        />
      </TabBody>
    );
  }
  return <PeriodAwards {...props} />;
}

/**
 * The notices behind the period's award bar. The server picks them with the chart's own rules,
 * which a notice search can't express (awarded stages only for mentions, no undated notices).
 */
function PeriodAwards({
  buyerGuid,
  supplierGuid,
  supplierMentionType,
  publishedFrom,
  period,
}: AwardsTabProps) {
  const [sort, setSort] = useState<SortState>({ field: "publish_date", order: "DESC" });
  const { data: noticeIds, isError } = useBuyerSupplierAwardPeriodNotices({
    buyerGuid,
    supplierGuid,
    interval: period.interval,
    period: period.start,
    publishedFrom,
    supplierMentionType,
  });

  const filters: NoticeFilters | undefined = useMemo(
    () =>
      noticeIds && {
        ...EMPTY_FILTERS,
        noticeIds,
        // The bar counts awards whatever the team marked them, so the list shows them all too.
        includeHiddenNotices: true,
        sort,
      },
    [noticeIds, sort],
  );

  if (isError) {
    return (
      <TabBody>
        <EmptyState
          type="loadError"
          heading="Couldn't load these awards"
          description="Something went wrong. Close this and try again."
          width="100%"
          yAxisMargin="64px"
        />
      </TabBody>
    );
  }

  // Only if the index moved since the chart loaded. An empty ID list would mean "no filter" to the
  // notice search, so it never reaches the table.
  if (filters && !filters.noticeIds?.length) {
    return (
      <TabBody>
        <EmptyState
          type="noResults"
          heading={`No awards in ${period.label}`}
          description="These awards have changed since the chart loaded. Close this and try again."
          width="100%"
          yAxisMargin="64px"
        />
      </TabBody>
    );
  }

  // The summary is known from the bar, so it stays put while the list loads beneath it.
  return (
    <TabBody>
      <Toolbar>
        <Text>{awardSummary(period)}</Text>
        {filters && <ExportNoticesButton filters={filters} />}
      </Toolbar>
      {filters ? (
        <NoticeTable columnSettings={AWARD_COLUMNS} filters={filters} onSortChange={setSort} />
      ) : (
        <LoadingRows aria-label="Loading awards">
          {LOADING_ROWS.map((row) => (
            <SkeletonText key={row} width="100%" shimmer />
          ))}
        </LoadingRows>
      )}
    </TabBody>
  );
}

const LOADING_ROWS = [1, 2, 3, 4, 5, 6];

/** Matches the bar: a shared award counts at this supplier's share, so it can differ from the rows. */
function awardSummary({ awardCount, awardValue }: ChartPeriod): string {
  const awards = awardCount === 1 ? "1 award" : `${awardCount} awards`;
  if (!awardValue) {
    return `${awards}, none with a published value`;
  }
  return `${awards} worth ${formatSpendValue(awardValue)} to this supplier`;
}

const Layout = styled.div({
  display: "flex",
  flexDirection: "column",
  maxHeight: MAX_HEIGHT,
});

const Header = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  // Clear of the modal's close button in the top right corner.
  padding: "24px 56px 16px 24px",
});

// The tabs fill what the header leaves, so their content area scrolls rather than the modal.
const TabsArea = styled.div({
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0,
  "& > *": { flex: "1 1 auto", minHeight: 0 },
  // The tab list pads its full width, so without this it overflows the modal by its padding.
  "& [role=tablist]": { boxSizing: "border-box" },
});

const TabBody = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: 24,
});

const LoadingRows = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const Toolbar = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
});
